import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Trophy, CheckCircle2, FolderKanban, Target, Sparkles, Loader2, ArrowRight, Check, AlertTriangle, Play, Flame, Gauge, TrendingUp } from "lucide-react";
import api from "../lib/apiClient";
import Progression from "./Progression";
import { celebrate } from "../components/CelebrationOverlay";
import { useAuth } from "../context/AuthContext";
import { XpBar } from "../components/XpBar";
import { fmtDate, CATEGORY_META } from "../lib/gameData";
import { toast } from "sonner";

export default function Dashboard() {
  const { refresh, identity } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [actions, setActions] = useState(null);
  const [loadingActions, setLoadingActions] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/dashboard");
    setData(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadActions = async () => {
    setLoadingActions(true);
    try {
      const { data } = await api.get("/assistant/next-actions");
      setActions(data.actions);
    } finally {
      setLoadingActions(false);
    }
  };

  const completeTask = async (task) => {
    setCompleting(task.id);
    try {
      const { data: res } = await api.put(`/tasks/${task.id}`, { status: "Terminée" });
      toast.success(`Tâche terminée · +${res.xp_awarded} XP`);
      if (res.leveled_up) celebrate("level", `Niveau ${res.level ?? ""}`);
      (res.new_badges || []).forEach(() => celebrate("badge"));
      await load();
      await refresh();
    } finally {
      setCompleting(null);
    }
  };

  if (!data) return <Center />;

  const lvl = data.level_info;

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <p className="text-sm font-mono uppercase tracking-wider text-amber-500">{identity?.space_label || "Mon espace"}</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1" data-testid="dashboard-greeting">{data.artist_name} 👋</h1>
        {identity?.profile_label && <p className="text-xs text-zinc-500 mt-1" data-testid="dashboard-profile-label">Profil : {identity.profile_label}</p>}
      </motion.div>

      {/* Que dois-je faire maintenant */}
      <div className="bg-gradient-to-br from-amber-500/15 to-transparent border border-amber-500/30 rounded-3xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-11 h-11 rounded-xl gold-gradient flex items-center justify-center glow-gold shrink-0">
              <Flame className="w-6 h-6 text-black" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold">Que dois-je faire maintenant ?</h3>
              <p className="text-xs text-zinc-400">Ton assistant analyse tes projets et te donne tes priorités.</p>
            </div>
          </div>
          <button onClick={loadActions} disabled={loadingActions} data-testid="what-now-btn"
            className="h-11 px-5 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60 shrink-0">
            {loadingActions ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Sparkles className="w-5 h-5" /> Analyser</>}
          </button>
        </div>
        {actions && (
          <div className="mt-4 space-y-2" data-testid="next-actions">
            {actions.length === 0 ? (
              <p className="text-sm text-zinc-400">Aucune action en attente. Crée un projet avec l'Assistant pour démarrer !</p>
            ) : actions.map((a, i) => {
              const style = a.priority === "Haute" ? "text-red-400 border-red-500/40" : a.priority === "Moyenne" ? "text-amber-400 border-amber-500/40" : "text-emerald-400 border-emerald-500/40";
              return (
                <motion.div key={a.task_id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 bg-[#0F0F13] border border-zinc-800 rounded-2xl p-3" data-testid={`action-${i}`}>
                  <span className="font-display font-extrabold text-lg text-amber-400 w-6 text-center shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{a.title}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs">
                      <span className={`px-2 py-0.5 rounded-full border ${style}`}>{a.priority}</span>
                      {a.overdue && <span className="text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> En retard</span>}
                      <span className="font-mono text-amber-400">+{a.xp} XP</span>
                    </div>
                  </div>
                  <button onClick={() => navigate("/tasks")} data-testid={`start-action-${i}`}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-amber-500/15 text-amber-400 border border-amber-500/30 text-xs font-medium hover:bg-amber-500/25">
                    <Play className="w-3.5 h-3.5" /> Commencer
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        {/* Recommended action */}
        <Card className="card-hover relative overflow-hidden border-amber-500/30" testid="recommendation-card">
          <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-amber-500/10 blur-2xl" />
          <div className="flex items-center gap-2 text-amber-400 mb-3 relative z-10">
            <Sparkles className="w-5 h-5" />
            <span className="text-xs font-mono uppercase tracking-wider">Action recommandée</span>
          </div>
          <h3 className="font-display text-lg font-bold relative z-10">{data.recommendation.title}</h3>
          <p className="text-sm text-zinc-400 mt-1 relative z-10">{data.recommendation.detail}</p>
          <button onClick={() => navigate("/assistant")} data-testid="reco-open-assistant"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-amber-400 hover:gap-2.5 transition-all relative z-10">
            Ouvrir l'assistant <ArrowRight className="w-4 h-4" />
          </button>
        </Card>
      </div>

      {/* Current goal */}
      {data.goal && (
        <Card testid="goal-card">
          <div className="flex items-center gap-2 text-zinc-400 mb-3">
            <Target className="w-5 h-5 text-amber-400" />
            <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Objectif actuel</span>
          </div>
          <h3 className="font-display text-xl font-bold">{data.goal}</h3>
          <div className="mt-4 flex items-center gap-4">
            <XpBar value={data.goal_progress} max={100} className="flex-1" />
            <span className="font-mono text-sm text-amber-400 shrink-0">{data.goal_progress}%</span>
          </div>
        </Card>
      )}

      {/* Cehnura AI */}
      <button onClick={() => navigate("/coach")} data-testid="dashboard-ai-card"
        className="w-full text-left bg-[#18181E] border border-zinc-800 rounded-3xl p-5 card-hover flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl gold-gradient flex items-center justify-center shrink-0 glow-gold"><Sparkles className="w-6 h-6 text-black" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg font-bold">Cehnura AI</h3>
          <p className="text-sm text-zinc-500 truncate">Que veux-tu faire ? Demande à ton intelligence de carrière.</p>
        </div>
        <span className="text-amber-400 text-sm shrink-0">Ouvrir →</span>
      </button>

      {/* Prochaine sortie */}
      {data.next_release ? (
        <button onClick={() => navigate(`/releases/${data.next_release.id}`)} data-testid="dashboard-next-release"
          className="w-full text-left bg-gradient-to-br from-amber-500/15 to-transparent border border-amber-500/30 rounded-3xl p-5 card-hover">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <span className="text-xs font-mono uppercase tracking-wider text-amber-500">🎵 Prochaine sortie</span>
              <h3 className="font-display text-xl font-bold mt-1">{data.next_release.title}</h3>
              <p className="text-sm text-zinc-400 mt-0.5">Sortie dans {data.next_release.days_remaining} j · Readiness {data.next_release.readiness}/100 · <span style={{ color: data.next_release.risk === "high" ? "#FB7185" : data.next_release.risk === "medium" ? "#F59E0B" : "#34D399" }}>{data.next_release.risk === "high" ? "🔴" : data.next_release.risk === "medium" ? "🟠" : "🟢"}</span></p>
              {data.next_release.next_action && <p className="text-xs text-amber-400 mt-2">Prochaine action : {data.next_release.next_action.title}</p>}
            </div>
            <span className="px-4 h-10 inline-flex items-center rounded-xl gold-gradient text-black font-semibold text-sm">Continuer</span>
          </div>
        </button>
      ) : (
        <button onClick={() => navigate("/assistant")} data-testid="dashboard-no-release" className="w-full text-left bg-[#18181E] border border-zinc-800 rounded-3xl p-5 card-hover">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-500">🚀 Prochain projet</span>
          <p className="text-sm text-zinc-400 mt-1">Planifie ton prochain projet avec l'assistant — <span className="text-amber-400">Créer un plan →</span></p>
        </button>
      )}

      {/* Ma journée + À rattraper */}
      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2" testid="ma-journee">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Play className="w-4 h-4 text-amber-400" /><span className="text-xs font-mono uppercase tracking-wider text-amber-500">Ma journée</span></div>
            {data.journee_minutes > 0 && <span className="text-xs text-zinc-400 font-mono">Total : {Math.floor(data.journee_minutes / 60)}h{String(data.journee_minutes % 60).padStart(2, "0")}</span>}
          </div>
          {data.ma_journee.length === 0 ? (
            <p className="text-sm text-zinc-500">Rien de prévu aujourd'hui. Planifie une tâche dans le calendrier !</p>
          ) : (
            <div className="space-y-2">
              {data.ma_journee.map((t) => (
                <div key={t.id} className="flex items-center gap-3 bg-[#0F0F13] border border-zinc-800 rounded-xl p-3" data-testid={`journee-task-${t.id}`}>
                  <span className="font-mono text-sm text-amber-400 w-12 shrink-0">{t.time || "—"}</span>
                  <span className="flex-1 min-w-0 text-sm truncate">{t.title}</span>
                  {t.duration_min ? <span className="text-xs text-zinc-500 shrink-0">{t.duration_min} min</span> : null}
                  <span className="font-mono text-xs text-amber-400 shrink-0">+{t.xp} XP</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className={data.a_rattraper_count > 0 ? "border-red-500/30" : ""} testid="a-rattraper">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle className={`w-4 h-4 ${data.a_rattraper_count > 0 ? "text-red-400" : "text-zinc-500"}`} /><span className="text-xs font-mono uppercase tracking-wider text-amber-500">À rattraper</span></div>
          {data.a_rattraper_count === 0 ? (
            <p className="text-sm text-zinc-500">Aucun retard, tu gères ! 💪</p>
          ) : (
            <>
              <p className="text-sm text-red-300 mb-2">{data.a_rattraper_count} tâche(s) en retard</p>
              <div className="space-y-1.5">
                {data.a_rattraper.map((t) => (
                  <div key={t.id} className="text-xs text-zinc-300 flex items-center gap-2" data-testid={`retard-${t.id}`}>
                    <span className="text-red-400">🔴</span><span className="truncate">{t.title}</span>
                    <span className="text-zinc-600 shrink-0">· {t.overdue_days}j</span>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate("/calendar")} data-testid="see-overdue-btn" className="mt-3 text-sm text-amber-400 hover:underline">Voir mes retards →</button>
            </>
          )}
        </Card>
      </div>

      {/* Today's tasks */}
      <div>
        <h2 className="font-display text-xl font-bold mb-4">Mes tâches du jour</h2>
        {data.today_tasks.length === 0 ? (
          <Card testid="today-empty">
            <p className="text-zinc-500 text-sm">Aucune tâche pour aujourd'hui. Crée un projet et ajoute des tâches pour progresser !</p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="today-tasks">
            {data.today_tasks.map((t) => (
              <motion.div key={t.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4 flex items-center gap-4 card-hover" data-testid={`today-task-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{t.title}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                    <span>{fmtDate(t.deadline)}</span>
                    {t.project_title && <span className="text-amber-500/80">· {t.project_title}</span>}
                    <span className="font-mono text-amber-400">+{t.xp} XP</span>
                  </div>
                </div>
                <button onClick={() => completeTask(t)} disabled={completing === t.id} data-testid={`complete-task-${t.id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-sm font-medium hover:bg-emerald-500/25 transition-colors disabled:opacity-50">
                  {completing === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Terminer
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-zinc-800" data-testid="dashboard-progression">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-amber-400" />
          <h2 className="font-display text-xl font-bold">Ma progression</h2>
        </div>
        <Progression />
      </div>
    </div>
  );
}

function Card({ children, className = "", testid }) {
  return <div data-testid={testid} className={`bg-[#18181E] border border-zinc-800 rounded-3xl p-6 relative ${className}`}>{children}</div>;
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4 card-hover" data-testid={testid}>
      <Icon className="w-5 h-5 text-amber-400 mb-3" />
      <p className="font-display text-2xl font-extrabold">{value}</p>
      <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
    </div>
  );
}

function Center() {
  return <div className="flex items-center justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;
}
